/**
 * Reiter „Woher die Anmeldungen kamen" der Sektions-Detailseite (Persona-Umbau B9, 28.10.2026, Plan
 * 2026-10-28-persona-umbau-paket-1-2). Dieselben Daten wie die alte Gruppierung (`GroupedSections`, bleibt für die alte
 * Seite unverändert), aber mit den Begriffen der Sektion (GLOSSAR/TERMS) statt „CPA-Wert / Anfo-Wert / Umsatz / Reg. /
 * Teiln.":
 *  - Anmeldungen, Teilnehmer, Show-up, Anfos, Anfos je 100 Anmeldungen, Hochrechnung, Bisher bezahlt. Der CPA-Wert
 *    entfällt (Abrechnungsgröße der alten Seite). Der Euro-Wert je Zeile ist die HOCHRECHNUNG aus den bisherigen
 *    Zahlungen (`ltv_revenue` der Gruppierung, Summe = Kopf „Hochrechnung"), nicht der Erwartete Wert — die Gruppierung
 *    liefert keinen Erwarteten Wert je Zeile.
 *  - Zeilen mit unter 5 Anfos grau; ihre Quoten und Euro-Werte sortieren ans Ende (Leitregel 4).
 *  - Restposten („Sonstige", „Kauf nach Sendestart, ohne Anmeldung" …) stehen immer am Ende.
 */
import { FC, ReactNode } from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import SortableTable, { Spalte } from '../../../components/auswertungen/SortableTable';
import { ENG_UNTER, useBreitePx } from '../../../components/auswertungen/kennzahlTeile';
import { formatEur, formatPct, TAIL_CATEGORIES, TERMS } from '../../../helpers/kpiDefinitions';
import { formatNumber } from '../../../helpers/numberUtils';
import type { EvaluationMetrics, MatrixCell, WebinarGroupedEvaluationDetailData } from '../../../helpers/webinarStatisticsData';

/** Unter so vielen Anfos ist eine Zeile grau (Plan B9). */
export const MIN_ANFOS_ZEILE = 5;

const HEAT_UP_ORDER = ['1', '2', '3', '4+', 'Ohne Nr.'];
/** Reihenfolge wie `GroupedSections` (`webinarEvaluation.ts` SIGNUP_LEAD_BUCKETS). */
const SIGNUP_LEAD_ORDER = [
  'mehr als 3 Tage vorher',
  '1–3 Tage vorher',
  '6–24 h vorher',
  '1–6 h vorher',
  'letzte Stunde vorher',
  'bis 2 h nach Start',
  '2–24 h nach Start',
  'später als 24 h nach Start',
  'Anmeldezeit unsicher (vor Mai 2026)',
];

const istRest = (c: string) => TAIL_CATEGORIES.includes(c);
const wenig = (r: EvaluationMetrics) => (r.orders < MIN_ANFOS_ZEILE ? `unter ${MIN_ANFOS_ZEILE} Anfos` : null);
const festeAnfos = (r: Partial<EvaluationMetrics>) => (r.orders ?? 0) - (r.orders_provisional ?? 0);

/** Kennzahl-Spalten der Sektion; `showUpGrund`: Teilnahme des Termins nicht belastbar → Show-up „—". */
function kennzahlSpalten<T extends EvaluationMetrics>(showUpGrund: string | null, summe: Partial<EvaluationMetrics> | null, schmal: boolean): Spalte<T>[] {
  const alle: Spalte<T>[] = [
    {
      key: 'anm',
      label: TERMS.anmeldungenLive.label,
      tip: TERMS.anmeldungenLive.tip,
      align: 'right',
      render: (r) => r.registrations.toLocaleString('de-DE'),
      sortValue: (r) => r.registrations,
      summary: summe ? (summe.registrations ?? 0).toLocaleString('de-DE') : undefined,
    },
    {
      key: 'teiln',
      label: TERMS.teilnehmer.label,
      tip: TERMS.teilnehmer.tip,
      align: 'right',
      render: (r) => (showUpGrund ? '–' : r.attendees.toLocaleString('de-DE')),
      sortValue: (r) => (showUpGrund ? null : r.attendees),
      summary: summe && !showUpGrund ? (summe.attendees ?? 0).toLocaleString('de-DE') : undefined,
    },
    {
      key: 'showup',
      label: TERMS.showUpTermin.label,
      einheit: TERMS.showUpTermin.einheit,
      tip: TERMS.showUpTermin.tip,
      align: 'right',
      render: (r) =>
        showUpGrund ? (
          <Tooltip title={showUpGrund} arrow placement="top">
            <Box component="span" sx={{ cursor: 'help' }}>
              —
            </Box>
          </Tooltip>
        ) : r.show_up_rate === null ? (
          '–'
        ) : (
          formatPct(r.show_up_rate, 0)
        ),
      sortValue: (r) => (showUpGrund ? null : r.show_up_rate),
      summary: summe && !showUpGrund && summe.show_up_rate != null ? formatPct(summe.show_up_rate, 0) : undefined,
    },
    {
      key: 'anfos',
      label: TERMS.anfos.label,
      tip: TERMS.anfos.tip,
      align: 'right',
      render: (r) => r.orders.toLocaleString('de-DE'),
      sortValue: (r) => r.orders,
      summary: summe ? (summe.orders ?? 0).toLocaleString('de-DE') : undefined,
    },
    {
      key: 'a100',
      label: TERMS.anfosJe100Anm.label,
      tip: `${TERMS.anfosJe100Anm.tip} Ohne vorläufig ergänzte Anfos.`,
      align: 'right',
      unsicher: wenig,
      render: (r) => (r.registrations > 0 ? formatNumber((festeAnfos(r) * 100) / r.registrations, 1) : '–'),
      sortValue: (r) => (r.registrations > 0 ? festeAnfos(r) / r.registrations : null),
      summary: summe && (summe.registrations ?? 0) > 0 ? formatNumber((festeAnfos(summe) * 100) / (summe.registrations as number), 1) : undefined,
    },
    {
      key: 'hoch',
      label: TERMS.hochrechnung.short,
      einheit: 'netto',
      tip: `${TERMS.hochrechnung.label}: ${TERMS.hochrechnung.tip} Hier je Zeile; die Summe ist die Hochrechnung im Kopf der Seite. Den Erwarteten Wert (bei Bestellung) liefert die Gruppierung nicht je Zeile.`,
      align: 'right',
      unsicher: wenig,
      render: (r) => formatEur(r.ltv_revenue, 0),
      sortValue: (r) => r.ltv_revenue,
      summary: summe ? formatEur(summe.ltv_revenue ?? 0, 0) : undefined,
    },
    {
      key: 'bezahlt',
      label: TERMS.umsatzNetto.label,
      tip: TERMS.umsatzNetto.tip,
      align: 'right',
      unsicher: wenig,
      render: (r) => formatEur(r.paid_revenue, 0),
      sortValue: (r) => r.paid_revenue,
      summary: summe ? formatEur(summe.paid_revenue ?? 0, 0) : undefined,
    },
  ];
  // Schmal (Handy, Karte ~270 px): nur die Quote als Spalte; Anmeldungen, Anfos und Hochrechnung stehen unter dem Namen
  // (gemessen 28.10.: mit vier Zahlenspalten 95–137 px Überlauf je Tabelle).
  return schmal ? alle.filter((s) => s.key === 'a100') : alle;
}

/** Schmal: Anmeldungen · Anfos · Hochrechnung klein unter dem Namen (ohne eigene Spalten). */
const SchmalZeile: FC<{ r: EvaluationMetrics }> = ({ r }) => (
  <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', whiteSpace: 'normal' }}>
    {r.registrations.toLocaleString('de-DE')} Anm. · {r.orders.toLocaleString('de-DE')} {r.orders === 1 ? 'Anfo' : 'Anfos'} · {TERMS.hochrechnung.short} {formatEur(r.ltv_revenue, 0)}
  </Box>
);

const Abschnitt: FC<{ titel: string; text?: string; children: ReactNode }> = ({ titel, text, children }) => (
  <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
    <Typography variant="h6" sx={{ color: 'black', fontWeight: 600, mb: text ? 0.5 : 1 }}>
      {titel}
    </Typography>
    {text && (
      <Typography variant="body2" sx={{ color: '#555', mb: 1 }}>
        {text}
      </Typography>
    )}
    {children}
  </Paper>
);

const Tabelle: FC<{
  rows: EvaluationMetrics[];
  name: string;
  showUpGrund: string | null;
  summe: Partial<EvaluationMetrics> | null;
  summeLabel?: string;
  rang?: (c: string) => number;
}> = ({ rows, name, showUpGrund, summe, summeLabel = 'Alle', rang }) => {
  const [ref, px] = useBreitePx();
  const schmal = px !== null && px < 640;
  const spalten: Spalte<EvaluationMetrics>[] = [
    {
      key: 'kategorie',
      label: name,
      // Kein Umbruch mitten im Wort („Finanztrend|s" bei 1.024 px, Runde 2): Mindestbreite, Umbruch nur bei Überlänge.
      minWidth: 110,
      render: (r) => (
        <Box sx={{ lineHeight: 1.3, overflowWrap: 'break-word', maxWidth: 360 }}>
          {r.category}
          {schmal && <SchmalZeile r={r} />}
        </Box>
      ),
      sortValue: (r) => (rang ? rang(r.category) : r.category),
    },
    ...kennzahlSpalten<EvaluationMetrics>(showUpGrund, summe, schmal),
  ];
  return (
    <Box ref={ref}>
      <SortableTable
        // Schmal fehlt die Spalte „Anmeldungen" (Standardsortierung) — dann vorsortiert nach Anmeldungen.
        rows={schmal && !rang ? [...rows].sort((a, b) => b.registrations - a.registrations) : rows}
        columns={spalten}
        rowKey={(r) => r.category}
        initialSort={rang ? { key: 'kategorie', dir: 'asc' } : { key: 'anm', dir: 'desc' }}
        abschnitt={(r) => (istRest(r.category) ? '' : null)}
        muted={(r) => wenig(r) !== null}
        eng={px !== null && px < ENG_UNTER}
        summaryLabel={summeLabel}
      />
    </Box>
  );
};

const SektionHerkunft: FC<{ data: WebinarGroupedEvaluationDetailData; showUpGrund: string | null; geraete: ReactNode }> = ({ data, showUpGrund, geraete }) => {
  const all = data.totals.all;
  const [ref, px] = useBreitePx();
  const schmal = px !== null && px < 640;
  const heatRang = (c: string) => {
    const i = HEAT_UP_ORDER.indexOf(c);
    return i < 0 ? HEAT_UP_ORDER.length : i;
  };
  const leadRang = (c: string) => {
    const i = SIGNUP_LEAD_ORDER.indexOf(c);
    return i < 0 ? SIGNUP_LEAD_ORDER.length : i;
  };
  const matrixSpalten: Spalte<MatrixCell>[] = [
    {
      key: 'ad',
      label: TERMS.werbeflaeche.label,
      tip: TERMS.werbeflaeche.tip,
      render: (r) => (
        <Box sx={{ lineHeight: 1.3, overflowWrap: 'anywhere' }}>
          {r.ad_type}
          {schmal && (
            <>
              {` · ${r.blog}`}
              <SchmalZeile r={r} />
            </>
          )}
        </Box>
      ),
      sortValue: (r) => r.ad_type,
    },
    ...(schmal ? [] : [{ key: 'blog', label: 'Blog', render: (r: MatrixCell) => r.blog, sortValue: (r: MatrixCell) => r.blog }]),
    ...kennzahlSpalten<MatrixCell>(showUpGrund, all, schmal),
  ];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }} ref={ref}>
      <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: '75ch' }}>
        Jede Person steht in genau einer Zeile — bei der Werbefläche ihrer ersten Anmeldung —, die Zeilen summieren also auf „Alle“. Grau: unter{' '}
        {MIN_ANFOS_ZEILE} Anfos, Quote und Euro hängen dort an Einzelfällen. Euro netto; „{TERMS.hochrechnung.short}“ ist die Hochrechnung aus den bisherigen
        Zahlungen, nicht der Erwartete Wert.
      </Typography>
      <Abschnitt titel={`Nach ${TERMS.werbeflaeche.label}`} text="Über welche Werbefläche die Personen kamen. Anfos ohne Anmeldung stehen in eigenen Zeilen am Ende.">
        <Tabelle rows={data.byAdType} name={TERMS.werbeflaeche.label} showUpGrund={showUpGrund} summe={all} />
      </Abschnitt>
      <Abschnitt titel="Nach Blog (Marke der Quelle)">
        <Tabelle rows={data.byBlog} name="Blog" showUpGrund={showUpGrund} summe={all} />
      </Abschnitt>
      {data.byHeatUpNummer.length > 0 && (
        <Abschnitt titel="Nach Heat-Up-Nummer" text="Nur Personen, die über ein Heat-Up-Mailing kamen. Summenzeile = alle Heat-Up-Personen.">
          <Tabelle rows={data.byHeatUpNummer} name="Heat-Up Nr." showUpGrund={showUpGrund} summe={data.totals.heatUp} summeLabel="Heat-Up gesamt" rang={heatRang} />
        </Abschnitt>
      )}
      {data.bySignupLead && data.bySignupLead.length > 0 && (
        <Abschnitt
          titel="Nach Anmeldezeitpunkt"
          text="Wie lange vor (oder nach) dem Start sich die Personen angemeldet haben — erste Anmeldung je Person. Show-up nur für Anmeldungen vor dem Start. Webinare vor Mai 2026 stehen gesammelt unter „Anmeldezeit unsicher“."
        >
          <Tabelle rows={data.bySignupLead} name="Anmeldung" showUpGrund={showUpGrund} summe={all} rang={leadRang} />
        </Abschnitt>
      )}
      <Abschnitt titel="Nach Affiliate" text="Kleine Affiliates sind zu „Sonstige“ zusammengefasst.">
        <Tabelle rows={data.byAffiliate} name="Affiliate" showUpGrund={showUpGrund} summe={all} />
      </Abschnitt>
      {geraete}
      <Abschnitt titel={`${TERMS.werbeflaeche.label} × Blog`}>
        <SortableTable
          rows={schmal ? [...data.matrix].sort((a, b) => b.registrations - a.registrations) : data.matrix}
          columns={matrixSpalten}
          rowKey={(r) => `${r.ad_type}|${r.blog}`}
          initialSort={{ key: 'anm', dir: 'desc' }}
          muted={(r) => wenig(r) !== null}
          eng={px !== null && px < ENG_UNTER}
          summaryLabel="Alle"
        />
      </Abschnitt>
    </Box>
  );
};

export default SektionHerkunft;
